import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, QrCode, Smartphone, MessageCircle, Camera } from "lucide-react";

const UPI_ID = "MATRUBHUMIGAURAS@SRCB";
const MERCHANT = "Matrubhumi Gauras Bhandar";
const UPI_DEEPLINK = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(MERCHANT)}&cu=INR`;
const WHATSAPP_NUMBER = "919819933676";
const WHATSAPP_MSG = "Namaste! Maine UPI/GPay se payment kar diya hai. Screenshot bhej raha hun. Please order confirm karein. 🙏";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

export default function UpiPayment() {
  const [copied, setCopied] = useState(false);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("Copy UPI ID failed:", err);
    }
  };

  return (
    <section id="upi-pay" className="dd-section bg-dd-page relative overflow-hidden" data-testid="upi-payment-section">
      {/* Decorative glows */}
      <div className="absolute top-0 left-0 w-[28rem] h-[28rem] opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(circle at top left, #C9A24C 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 right-0 w-[24rem] h-[24rem] opacity-15 pointer-events-none"
           style={{ background: "radial-gradient(circle at bottom right, #E6C97A 0%, transparent 60%)" }} />

      <div className="dd-container max-w-3xl relative">
        <div className="text-center mb-10">
          <div className="dd-label text-dd-gold">Instant Payment</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-cream mt-3 tracking-tight">
            Pay via GPay / UPI
          </h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-cream-muted text-sm md:text-base">
            Scan the QR with any UPI app — payment confirms in seconds.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto max-w-md"
        >
          {/* Outer gold glow */}
          <div className="absolute -inset-3 rounded-[26px] bg-gradient-to-br from-dd-gold/40 via-transparent to-dd-gold/30 blur-xl opacity-70 pointer-events-none" />

          <div className="relative bg-white rounded-[22px] border-2 border-dd-gold/60 shadow-[0_30px_80px_-20px_rgba(201,162,76,0.45)] p-7 md:p-8" data-testid="upi-qr-card">
            {/* Header chip */}
            <div className="flex items-center justify-center mb-5">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark text-[0.62rem] tracking-[0.22em] uppercase font-bold shadow-md">
                <QrCode className="w-3.5 h-3.5" strokeWidth={2.25} /> Scan &amp; Pay
              </div>
            </div>

            {/* QR */}
            <div className="bg-white border border-dd-line rounded-2xl p-3 md:p-4 flex items-center justify-center">
              <img
                src="/upi-qr.png"
                alt="UPI QR code for Matrubhumi Gauras Bhandar"
                className="w-full max-w-[280px] h-auto rounded-md"
                draggable={false}
                data-testid="upi-qr-image"
              />
            </div>

            {/* Merchant */}
            <div className="mt-5 text-center">
              <div className="text-xs tracking-[0.22em] uppercase text-[#8F6E28] font-semibold">Pay to</div>
              <div className="font-serif text-xl text-dd-ink mt-1">{MERCHANT}</div>
            </div>

            {/* UPI ID */}
            <div className="mt-4 flex items-center gap-2 bg-dd-ivory/[0.04] border border-dd-gold/40 rounded-full px-4 py-2.5">
              <span className="text-[0.62rem] tracking-[0.22em] uppercase text-[#8F6E28] font-semibold whitespace-nowrap">UPI ID</span>
              <code className="font-mono text-sm md:text-base text-dd-ink font-bold tracking-tight flex-1 truncate" data-testid="upi-id-text">{UPI_ID}</code>
              <button
                onClick={copyUpi}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#8F6E28] hover:text-dd-gold border border-dd-gold/40 hover:border-dd-gold rounded-full px-3 py-1 transition-colors"
                data-testid="upi-copy-btn"
                aria-label="Copy UPI ID"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            {/* Instructions */}
            <div className="mt-5 flex items-start gap-3 bg-dd-gold/5 border border-dd-gold/25 rounded-xl p-4">
              <Smartphone className="w-5 h-5 text-[#8F6E28] shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="text-sm text-dd-ink leading-relaxed">
                <span className="font-semibold">Open GPay / PhonePe / Paytm</span> &rarr; Scan QR &rarr; Pay instantly.
              </div>
            </div>

            {/* Screenshot note */}
            <div className="mt-3 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <Camera className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="text-sm text-dd-ink leading-relaxed">
                Take a <span className="font-semibold">screenshot after payment</span> and WhatsApp it to us — order confirms within minutes.
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa52] text-white px-5 py-3.5 rounded-full font-bold text-sm tracking-wide transition-colors shadow-lg shadow-[rgba(37,211,102,0.3)]"
              data-testid="upi-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.25} /> Send Payment Screenshot
            </a>

            {/* Mobile UPI deep-link — opens GPay/PhonePe/Paytm directly */}
            <a
              href={UPI_DEEPLINK}
              className="mt-2 w-full inline-flex md:hidden items-center justify-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark px-5 py-3.5 rounded-full font-bold text-sm tracking-wide transition-colors shadow-md"
              data-testid="upi-deeplink-btn"
            >
              <Smartphone className="w-5 h-5" strokeWidth={2.25} /> Pay in UPI App
            </a>
          </div>
        </motion.div>

        <p className="text-center text-xs text-dd-cream-muted mt-6 tracking-wide">
          Secure UPI via Saraswat Co-operative Bank · Powered by NPCI
        </p>
      </div>
    </section>
  );
}
